import React, { useEffect, useMemo, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import { Check } from "react-feather";
import { Button } from "@cinesys/ui/components";
import { SPOTTER_STRINGS } from "../strings";
import { PAGE_WRAP_VERTICAL_PADDING_PX } from "../pageLayout";
import { checkOrder, OrderCheckEntry } from "./checkOrder";
import { anchorJobParentLabel } from "./breadcrumbParent";
import { resolveScrollTargetUid } from "./spotterPickScroll";
import ReelStrip from "./anchorStage/ReelStrip";
import AnchorStage from "./anchorStage/AnchorStage";
import AnchorGamebookTable from "./anchorStage/AnchorGamebookTable";
import {
  AnchorJob,
  AnchorResolvedSource,
  AnchorTask,
  filmsGroundMutationErrorMessage,
  isOutOfOrderError,
  useSubmitAnchors,
} from "./service";
import {
  AnchorDraftState,
  EMPTY_ANCHOR_DRAFT,
  applyClipSubstitution,
  commitPlay,
  effectiveClipAssetId,
  undoClipSubstitution,
} from "./anchorResolutionDraft";

/** The manual-UID escape hatch's own parsing — moved here from `AnchorStage.tsx` alongside the
 *  rest of that entry's state/commit logic once its input+button moved into
 *  `AnchorGamebookTable.tsx`'s own header (this file already owns `handleCommit` and the draft
 *  state (`anchorResolutionDraft.ts`) the commit itself needs). */
const MANUAL_UID_PATTERN = /^P?(\d{1,3})$/i;

function normalizeManualUid(raw: string): string | null {
  const match = raw.trim().match(MANUAL_UID_PATTERN);
  if (!match) {
    return null;
  }
  return `P${match[1].padStart(3, "0")}`;
}

/**
 * Films Ground Phase B — full-page anchor editor ("stage over sheet" / Option A). Rendered
 * INLINE by `AnchorReviewPanel.tsx` in place of that panel's own status card once the customer
 * opts into editing (or immediately, read-only, once a job is `submitted`) — see that file's doc
 * comment for why entering this view is a state flip, not a route navigation.
 *
 * ONE anchor is on screen at a time — a reel of every clip across the top (`ReelStrip`) with the
 * current one focused, the clip and its context fixed above a full-width gamebook (`AnchorStage`
 * + `AnchorGamebookTable`). Nothing here scrolls except the gamebook band itself (`StageWrap`
 * below is height-capped to the viewport, same `calc(100vh - var(--spotterHeaderHeight))` budget
 * `ControlRoomScreen.tsx`'s own `RailPane` already uses, so the two stay visually aligned).
 *
 * UNSUBMITTED CONFIRMATIONS MUST SURVIVE EVERYTHING — a background poll, a status change, any
 * re-render, and navigating to another anchor and back. Two things make that true here:
 *   1. `drafts`/`currentAnchorId` only ever seed once per `job.jobId` (the `seededForJobId`
 *      guard below) — a background refetch of the SAME job (react-query's own window-focus
 *      refetch, or any other cache update) hands this component a new `job` object, but the guard
 *      means neither piece of state re-derives from it. A poll tick can only ever ADD data this
 *      component hasn't seen (impossible here anyway — `AnchorReviewPanel.tsx`'s poll effect stops
 *      once `job.status` leaves `"analyzing"`, well before this component ever mounts), never
 *      discard what's already been drafted. `drafts` (`anchorResolutionDraft.ts`) holds a picked
 *      play AND/OR a clip substitution per anchor — `AnchorStage.tsx` remounts per anchor
 *      (`key={displayedAnchor.anchorId}` below), so this is also the ONLY thing a substitution
 *      can survive off of; the badge it renders is always read back from here, never from local
 *      state of its own.
 *   2. This component's own mount identity never changes while working a job — `AnchorReviewPanel`
 *      mounts it with no `key` of its own (only the PARENT panel remounts, via `key={jobId}`, on a
 *      genuine job switch). `currentAnchorId` (which anchor is on screen) is plain local state, not
 *      derived from `job` on every render, so switching anchors — or `AnchorGamebookTable`/
 *      `AnchorStage` re-rendering under a poll-refreshed `job` prop — can never reset it either.
 */

/** `ControlRoomScreen.tsx`'s own page `Wrap` (`padding: 20px 22px 44px`) sits ABOVE this component
 *  in the tree and isn't itself height-capped — its 20px top + 44px bottom padding is real vertical
 *  space `StageWrap`'s own `100vh - headerHeight` budget below has to account for, or the page
 *  grows exactly that much taller than the viewport and genuinely scrolls. `RailPane`
 *  (`ControlRoomScreen.tsx`) sizes itself off the same formula and needs the identical correction —
 *  see `pageLayout.ts`, which both files import this constant from rather than each carrying its
 *  own literal `64`. */
const StageWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  height: calc(
    100vh - var(--spotterHeaderHeight, 118px) -
      ${PAGE_WRAP_VERTICAL_PADDING_PX}px
  );
  min-height: 0;

  --inputBackground: var(--spotterRaised);
  --inputBorder: var(--spotterLine2);
  --dark400: var(--spotterRaised);
  --dark500: var(--spotterLine2);
  --dark600: var(--spotterLine2);
  --dark700: var(--spotterInkFaint);
  --gray400: var(--spotterInk);
  --gray500: var(--spotterInk);
  --blue060: var(--spotterAccent);
  --blue100: var(--spotterAccent);
  --selectOptionHover: var(--spotterAccentHover);
  --blue400: var(--spotterAccent);
  --blue500: var(--spotterAccent);
  --blueBorder: var(--spotterAccentLine);
  --blueBorderOverlay: var(--spotterAccentLine);
  --blueBackground: var(--spotterAccentSoft);
  --blueFocusRing: var(--spotterAccentSoft);
  --green400: var(--spotterOk);
  --green500: var(--spotterOk);
  --greenBorder: var(--spotterOkLine);
  --greenBorderOverlay: var(--spotterOkLine);
  --greenBackground: var(--spotterOkSoft);
  --red400: var(--spotterDanger);
  --red500: var(--spotterDanger);
  --redBorder: var(--spotterDangerBorder);
  --redBorderOverlay: var(--spotterDangerBorder);
  --redBackground: var(--spotterDangerSoft);
`;

const TopStrip = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  flex: none;
`;

const Crumb = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
`;

const CrumbParent = styled.span`
  font-size: 11px;
  color: var(--spotterInkFaint);
  letter-spacing: 0.02em;
`;

const CrumbTitle = styled.h1`
  margin: 0;
  font-size: 19px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: var(--spotterInk);
`;

/** Fixed-height so the header never grows/shrinks/reflows as the submit state changes (Monday
 *  12957358700 follow-up, Addison: the old version stacked a `Banner` above this row on top of the
 *  progress bar/hint/button whenever locked or errored, which wrapped onto a second line and
 *  pushed `ReelStrip`/`AnchorStage`/`AnchorGamebookTable` below to reflow every time). Exactly ONE
 *  status message renders at a time (`SubmitStatusText`, tone-colored) instead of stacking a
 *  `Banner` on top of the hint text that already said the same thing — see `statusMessage` below. */
const SubmitBlock = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: nowrap;
  justify-content: flex-end;
  min-height: 34px;
`;

const SubmitProgress = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
`;

const SubmitProgressLabel = styled.span`
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--spotterInkFaint);
  font-family: ui-monospace, Menlo, monospace;
  font-weight: 700;
  white-space: nowrap;
`;

const SubmitProgressBar = styled.div`
  width: 100px;
  height: 6px;
  border-radius: 99px;
  background: var(--spotterLine2);
  overflow: hidden;
  flex-shrink: 0;
`;

const SubmitProgressBarFill = styled.i<{ $pct: number }>`
  display: block;
  height: 100%;
  width: ${(p) => Math.max(0, Math.min(100, p.$pct))}%;
  background: var(--spotterAccent);
  transition: width 0.3s;
`;

const SubmitProgressCount = styled.b`
  color: var(--spotterInk);
  font-variant-numeric: tabular-nums;
  font-size: 12.5px;
  white-space: nowrap;
`;

/** The header's ONE status message, shown ONLY once locked (there's no button left to hang a
 *  tooltip on at that point) — every other state's message (why the button's disabled, or that
 *  it's ready) lives in the Submit button's own `tooltip` instead (Addison: "Confirm all anchors
 *  to submit (5 left) and similar messages should be tooltips on button") rather than as
 *  always-visible text next to it. See `SubmitBlock`'s doc comment for why this whole area stays a
 *  single, fixed-height slot either way. */
const SubmitStatusText = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: var(--spotterOk);
  text-align: right;
  max-width: 280px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const slideInFromRight = keyframes`
  from { opacity: 0; transform: translateX(28px); }
  to { opacity: 1; transform: translateX(0); }
`;

const slideInFromLeft = keyframes`
  from { opacity: 0; transform: translateX(-28px); }
  to { opacity: 1; transform: translateX(0); }
`;

/** Wraps `AnchorStage` + `AnchorGamebookTable` together — structurally a transparent stand-in for
 *  the two flex items they used to be directly inside `StageWrap` (same `flex: 1; min-height: 0`
 *  a flex COLUMN sibling would need to fill the space they used to split between them; `AnchorStage`
 *  keeps its own `flex: 0 1 292px` and `AnchorGamebookTable` its own `flex: 1` inside here
 *  unchanged, one level deeper but under the same flex-column context).
 *
 *  The swipe (Addison: "going between clips... swipe it over based on if we're going forward or
 *  back in time") is driven IMPERATIVELY on this one wrapper (`slideStageRef`/the `useEffect`
 *  below), not via a remount-on-key-change trick — `AnchorGamebookTable` is deliberately never
 *  remounted between anchors (see its own file doc comment: it owns real scroll position / DOM
 *  refs that a remount would needlessly discard). Animating this shared ancestor's `transform`/
 *  `opacity` still visually carries both `AnchorStage` (which DOES remount per anchor, painting
 *  fresh into the same animating box) and the never-remounted gamebook table along together in one
 *  motion, without that remount. */
const SlideStage = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-height: 0;

  /* Unused as actual CSS properties — referencing both keyframes here is what makes
     styled-components inject their real @keyframes rules into the stylesheet at all, since the
     transition itself is applied imperatively (see the useEffect above), never through this
     template's own \`animation\` property. */
  --slide-in-from-right: ${slideInFromRight};
  --slide-in-from-left: ${slideInFromLeft};
`;

export interface AnchorConfirmContentProps {
  job: AnchorJob;
}

const AnchorConfirmContent = ({ job }: AnchorConfirmContentProps) => {
  const isSlowmoBand = job.reviewMode === "slowmo_band" && !!job.band;
  const t = isSlowmoBand
    ? {
        ...SPOTTER_STRINGS.filmsGround.phaseB,
        ...SPOTTER_STRINGS.filmsGround.phaseBSlowmoBand,
      }
    : SPOTTER_STRINGS.filmsGround.phaseB;

  // Draft resolution state, seeded ONCE per job (see file doc comment, point 1 — this is the load-
  // bearing invariant). `currentAnchorId` is seeded the same way, alongside it, for the same
  // reason: neither may re-derive from a later `job` prop update.
  const [drafts, setDrafts] = useState<Map<number, AnchorDraftState>>(
    new Map()
  );
  const [seededForJobId, setSeededForJobId] = useState<string | undefined>();
  const [currentAnchorId, setCurrentAnchorId] = useState<number>(0);
  // The manual-UID escape hatch's own draft state (`AnchorGamebookTable.tsx`'s header input) —
  // reset per anchor switch below, same as `AnchorStage.tsx`'s per-anchor transient UI state.
  const [manualValue, setManualValue] = useState("");
  const [manualInvalid, setManualInvalid] = useState(false);
  // Whether the reel's leading, non-interactive-to-EDIT game-start frame is what's showing in the
  // stage below, rather than a real anchor — a separate flag, not a `currentAnchorId` overload
  // (anchorId 0 is never a real anchor id, but `currentAnchorId` already falls back to `0` in the
  // "job has no anchors at all" degenerate case above, so reusing it here would conflate the two).
  const [viewingStart, setViewingStart] = useState(false);

  // `AnchorStage` used to own this state and fully remount (`key={anchorId}`) on every anchor
  // switch, which cleared it for free — lifted up here now, so it needs its own reset instead.
  useEffect(() => {
    setManualValue("");
    setManualInvalid(false);
  }, [currentAnchorId]);

  if (seededForJobId !== job.jobId) {
    const seeded = new Map<number, AnchorDraftState>();
    for (const anchor of job.anchors) {
      if (
        anchor.resolvedByUser &&
        anchor.resolvedUid &&
        anchor.resolvedSource
      ) {
        seeded.set(
          anchor.anchorId,
          commitPlay(
            anchor.resolvedUid,
            anchor.resolvedSource,
            anchor.resolvedClipAssetId ?? anchor.clipAssetId
          )
        );
      }
    }
    const firstUnresolved = job.anchors.find((a) => !seeded.has(a.anchorId));
    setDrafts(seeded);
    setCurrentAnchorId(
      firstUnresolved?.anchorId ?? job.anchors[0]?.anchorId ?? 0
    );
    setSeededForJobId(job.jobId);
  }

  const currentIndex = job.anchors.findIndex(
    (a) => a.anchorId === currentAnchorId
  );
  const currentAnchor = job.anchors[currentIndex] ?? job.anchors[0];

  const orderEntries: OrderCheckEntry[] = useMemo(() => {
    const entries: OrderCheckEntry[] = job.start
      ? [{ anchorId: 0, uid: job.start.uid }]
      : [];
    for (const anchor of job.anchors) {
      entries.push({
        anchorId: anchor.anchorId,
        uid: drafts.get(anchor.anchorId)?.resolution?.uid ?? null,
      });
    }
    return entries;
  }, [job, drafts]);

  const outOfOrderIds = useMemo(() => checkOrder(orderEntries), [orderEntries]);

  const resolvedUidByAnchorId = useMemo(() => {
    const map = new Map<number, string>();
    drafts.forEach((draft, anchorId) => {
      if (draft.resolution) {
        map.set(anchorId, draft.resolution.uid);
      }
    });
    return map;
  }, [drafts]);

  const totalAnchors = job.anchors.length;
  const doneCount = resolvedUidByAnchorId.size;
  const pct =
    totalAnchors > 0 ? Math.round((doneCount / totalAnchors) * 100) : 0;

  const submitMutation = useSubmitAnchors(job.jobId);

  // Same "lock on server state, not just this session's mutation" rule the pre-rebuild version
  // used — a reload after submitting still locks.
  const isLocked = job.status === "submitted" || submitMutation.isSuccess;

  const saveDisabledReason: string | null = isLocked
    ? t.alreadySubmitted
    : outOfOrderIds.size > 0
    ? t.outOfOrderBanner(outOfOrderIds.size)
    : doneCount < totalAnchors
    ? t.submitRemaining(totalAnchors - doneCount)
    : null;

  // Which way the swipe transition (`SlideStage`'s own doc comment) should play for the NEXT
  // anchor switch — set right alongside whatever triggered it (a reel click, a stepper arrow, or
  // `handleCommit`'s own auto-advance), read once by the `useEffect` below once the switch has
  // actually happened.
  const [stepDirection, setStepDirection] = useState<"next" | "prev" | null>(
    null
  );
  const slideStageRef = useRef<HTMLDivElement>(null);

  // Imperative, not a CSS `animation` tied to a remount key — see `SlideStage`'s own doc comment
  // for why `AnchorGamebookTable` must never remount. Resetting `animation` to `none` and forcing
  // a reflow before reapplying it (rather than just setting the new value) is what makes two
  // same-direction steps in a row (e.g. clicking "next" twice quickly) each restart the animation
  // instead of the second one being a no-op style change the browser ignores.
  useEffect(() => {
    const el = slideStageRef.current;
    if (!el || !stepDirection) {
      return;
    }
    const animationName =
      stepDirection === "next" ? slideInFromRight.name : slideInFromLeft.name;
    el.style.animation = "none";
    void el.offsetHeight;
    el.style.animation = `${animationName} 0.22s ease`;
  }, [currentAnchorId, viewingStart]);

  const goToAnchor = (anchorId: number) => {
    const targetIndex = job.anchors.findIndex((a) => a.anchorId === anchorId);
    setStepDirection(
      targetIndex < 0 || targetIndex === currentIndex
        ? null
        : targetIndex > currentIndex
        ? "next"
        : "prev"
    );
    setViewingStart(false);
    setCurrentAnchorId(anchorId);
  };

  // Game start sits chronologically before every real anchor — always "back in time" relative to
  // whichever anchor was on screen (Addison: "swipe it over based on if we're going forward or
  // back in time").
  const goToStart = () => {
    setStepDirection("prev");
    setViewingStart(true);
  };

  // Backs the small chevrons flanking "Anchor N / Total" in `AnchorStage.tsx` — a second way to
  // move between anchors besides clicking a `ReelStrip` card (Addison: "can you have arrow left,
  // arrow right on either side of that to move between the different anchors?").
  const stepAnchor = (offset: number) => {
    const target = job.anchors[currentIndex + offset];
    if (target) {
      goToAnchor(target.anchorId);
    }
  };
  const hasPrevAnchor = currentIndex > 0;
  const hasNextAnchor =
    currentIndex >= 0 && currentIndex < job.anchors.length - 1;

  const handleCommit = (
    uid: string,
    source: AnchorResolvedSource,
    clipAssetId: string
  ) => {
    const anchorId = currentAnchor.anchorId;
    const nextDrafts = new Map(drafts);
    nextDrafts.set(anchorId, commitPlay(uid, source, clipAssetId));
    setDrafts(nextDrafts);

    // Advance to the next still-unresolved anchor (same "keep moving down the list" motion the
    // pre-rebuild version's scroll-to-next had) — stays put if everything after this one is
    // already resolved, same as before. A clip-only substitution with no play yet still counts as
    // "not resolved" here (`?.resolution`, not just map presence).
    const committedIdx = job.anchors.findIndex((a) => a.anchorId === anchorId);
    const next = job.anchors
      .slice(committedIdx + 1)
      .find((a) => !nextDrafts.get(a.anchorId)?.resolution);
    if (next) {
      setStepDirection("next");
      setCurrentAnchorId(next.anchorId);
    }
  };

  // Stepping the reel to a different clip (`AnchorStage.tsx`'s `onSubstitute`) — always records
  // the substitution, per anchor. Passes `false` for "is the committed play still visible in the
  // new clip": the frontend has no per-play, per-clip visibility data (see
  // `anchorResolutionDraft.ts`'s doc comment), so this is the deliberate conservative branch — a
  // carried-over play always gets cleared rather than risking a UID paired with the wrong clip.
  const handleSubstitute = (clipAssetId: string) => {
    const anchorId = currentAnchor.anchorId;
    const draft = drafts.get(anchorId) ?? EMPTY_ANCHOR_DRAFT;
    const nextDrafts = new Map(drafts);
    nextDrafts.set(anchorId, applyClipSubstitution(draft, clipAssetId, false));
    setDrafts(nextDrafts);
  };

  const handleUndoSubstitution = () => {
    const anchorId = currentAnchor.anchorId;
    const draft = drafts.get(anchorId) ?? EMPTY_ANCHOR_DRAFT;
    const nextDrafts = new Map(drafts);
    nextDrafts.set(anchorId, undoClipSubstitution(draft));
    setDrafts(nextDrafts);
  };

  const handleManualSet = () => {
    const normalized = normalizeManualUid(manualValue);
    if (!normalized) {
      setManualInvalid(true);
      return;
    }
    setManualValue("");
    setManualInvalid(false);
    const draft = drafts.get(currentAnchor.anchorId) ?? EMPTY_ANCHOR_DRAFT;
    handleCommit(
      normalized,
      "manual",
      effectiveClipAssetId(draft, currentAnchor.clipAssetId)
    );
  };

  const handleSubmit = () => {
    if (saveDisabledReason) {
      return;
    }
    const anchors = job.anchors.map((anchor) => {
      const draft = drafts.get(anchor.anchorId) ?? EMPTY_ANCHOR_DRAFT;
      return {
        anchorId: anchor.anchorId,
        uid: draft.resolution?.uid ?? "",
        clipAssetId: effectiveClipAssetId(draft, anchor.clipAssetId),
        source: draft.resolution?.source ?? "picker",
      };
    });
    submitMutation.mutate(anchors);
  };

  // A stand-in `AnchorTask` for the game-start boundary — lets `AnchorStage` render its clip the
  // same way it renders any real anchor's, rather than that component needing its own separate
  // "start" code path. Always shown locked (see `displayedLocked` below): the start boundary is
  // immutable from Phase B regardless of whether the REST of the job is locked.
  const startAsAnchor: AnchorTask | null = job.start
    ? {
        anchorId: 0,
        clipAssetId: job.start.assetId,
        clipName: undefined,
        spotterPickUid: job.start.uid,
        gamebookWindow: [],
        resolvedUid: job.start.uid,
        resolvedClipAssetId: job.start.assetId,
        resolvedByUser: true,
        resolvedSource: "manual",
      }
    : null;
  const showingStart = viewingStart && !!startAsAnchor;
  const displayedLocked = showingStart ? true : isLocked;

  const scrollToUid = showingStart
    ? job.start?.uid ?? null
    : currentAnchor
    ? resolveScrollTargetUid(
        currentAnchor.anchorId,
        currentAnchor.spotterPickUid,
        orderEntries
      )
    : null;

  const sourceTitle =
    job.source.kind === "assets"
      ? SPOTTER_STRINGS.controlRoom.assetsCount(job.source.assetCount ?? 0)
      : job.source.name ?? job.gameLabel;
  const parentLabel = anchorJobParentLabel(
    job.source.kind,
    job.parentCollectionName
  );

  if (!currentAnchor) {
    return null;
  }

  const displayedAnchor: AnchorTask =
    showingStart && startAsAnchor ? startAsAnchor : currentAnchor;

  // A genuine submit failure (the out-of-order case already has its own dedicated banner down in
  // `AnchorStage.tsx`'s count-based one, so it's excluded here to avoid saying the same thing
  // twice) wins the Submit button's tooltip over the plain disabled-reason/ready-to-send text.
  const hasGenericSubmitError =
    submitMutation.isError && !isOutOfOrderError(submitMutation.error);
  const submitTooltip = hasGenericSubmitError
    ? filmsGroundMutationErrorMessage(submitMutation.error, t.submitError)
    : saveDisabledReason ?? t.submitAllConfirmed;

  // The current anchor's draft, and the clip it's presently committed to — read off the same
  // `anchorResolutionDraft.ts` functions the submit payload uses, so `AnchorStage.tsx`'s badge and
  // the actual outbound `clipAssetId` can never disagree.
  const currentDraft = drafts.get(currentAnchor.anchorId) ?? EMPTY_ANCHOR_DRAFT;
  const currentEffectiveClipAssetId = effectiveClipAssetId(
    currentDraft,
    currentAnchor.clipAssetId
  );
  const resolvedClipAssetIdForStage =
    !showingStart && currentEffectiveClipAssetId !== currentAnchor.clipAssetId
      ? currentEffectiveClipAssetId
      : null;

  return (
    <StageWrap>
      <TopStrip>
        <Crumb>
          {parentLabel && (
            <CrumbParent>
              {parentLabel} {t.breadcrumbSeparator}
            </CrumbParent>
          )}
          <CrumbTitle>{sourceTitle}</CrumbTitle>
        </Crumb>

        <SubmitBlock>
          {/* Once locked, this can never be submitted again from here — no point still showing a
              progress bar (permanently full) or a Submit button (permanently disabled). See file
              doc comment. */}
          {isLocked ? (
            <SubmitStatusText>{t.submitSuccess}</SubmitStatusText>
          ) : (
            <>
              <SubmitProgress>
                <SubmitProgressLabel>
                  {t.headerProgressLabel}
                </SubmitProgressLabel>
                <SubmitProgressBar>
                  <SubmitProgressBarFill $pct={pct} />
                </SubmitProgressBar>
                <SubmitProgressCount>
                  {doneCount}/{totalAnchors}
                </SubmitProgressCount>
              </SubmitProgress>
              {/* Success-styled with a check once everything's actually resolved and orderly —
                  Addison: "once I've selected everything, submit... should be a success button.
                  And it should have a check as its icon." Stays the plain/default treatment
                  while `saveDisabledReason` still has something left to fix. */}
              <Button
                label={submitMutation.isLoading ? t.submitting : t.submitButton}
                type={saveDisabledReason ? "default" : "success"}
                icon={Check}
                tooltip={submitTooltip}
                onClick={handleSubmit}
                disabled={!!saveDisabledReason || submitMutation.isLoading}
                loading={submitMutation.isLoading}
              />
            </>
          )}
        </SubmitBlock>
      </TopStrip>

      <ReelStrip
        jobId={job.jobId}
        start={job.start}
        startFocused={showingStart}
        onSelectStart={goToStart}
        anchors={job.anchors}
        currentAnchorId={showingStart ? -1 : currentAnchor.anchorId}
        resolvedUidByAnchorId={resolvedUidByAnchorId}
        outOfOrderIds={outOfOrderIds}
        onSelect={goToAnchor}
      />

      <SlideStage ref={slideStageRef}>
        <AnchorStage
          key={displayedAnchor.anchorId}
          jobId={job.jobId}
          anchor={displayedAnchor}
          totalAnchors={totalAnchors}
          isSlowmoBand={isSlowmoBand}
          outOfOrderCount={outOfOrderIds.size}
          locked={displayedLocked}
          resolved={
            showingStart || resolvedUidByAnchorId.has(currentAnchor.anchorId)
          }
          resolvedClipAssetId={resolvedClipAssetIdForStage}
          onSubstitute={handleSubstitute}
          onUndoSubstitution={handleUndoSubstitution}
          isStartFrame={showingStart}
          onPrevAnchor={() => stepAnchor(-1)}
          onNextAnchor={() => stepAnchor(1)}
          hasPrevAnchor={hasPrevAnchor}
          hasNextAnchor={hasNextAnchor}
        />

        <AnchorGamebookTable
          plays={job.plays}
          gameLabel={job.gameLabel}
          suggestedUid={displayedAnchor.spotterPickUid}
          scrollToUid={scrollToUid}
          locked={displayedLocked}
          onSelect={(play) =>
            handleCommit(play.uid, "picker", currentEffectiveClipAssetId)
          }
          manualUid={{
            value: manualValue,
            invalid: manualInvalid,
            onChange: (v) => {
              setManualValue(v);
              setManualInvalid(false);
            },
            onSubmit: handleManualSet,
          }}
        />
      </SlideStage>
    </StageWrap>
  );
};

export default AnchorConfirmContent;
